import { Navigate, Route, Routes } from "react-router-dom";
import { useMe } from "./api/queries.js";
import { useAuth } from "./auth/AuthProvider.jsx";
import { Layout } from "./components/Layout.js";
import { useEmbedHeight } from "./embed/useEmbedHeight.js";
import { Alert, Loading } from "./components/ui.js";
import { Availability } from "./routes/Availability.js";
import { ConsultationDetail } from "./routes/ConsultationDetail.js";
import { Consultations } from "./routes/Consultations.js";
import { Queue } from "./routes/Queue.js";
import { SignIn } from "./routes/SignIn.js";
import { SpecialistDetail } from "./routes/SpecialistDetail.js";
import { Staff } from "./routes/Staff.js";
import { Specialists } from "./routes/Specialists.js";

/**
 * Where a signed-in user lands.
 *
 * A specialist and a coordinator both open on work waiting for them; a patient
 * opens on their own consultations. Nobody should have to navigate to find the
 * thing they came for.
 */
function Home() {
  const { data: me, isPending } = useMe();

  if (isPending) return <Loading />;
  if (me?.roles.includes("admin") === true) return <Navigate to="/queue" replace />;

  return <Navigate to="/consultations" replace />;
}

/**
 * A confirmed account that nobody has put in a group yet. Rare, but the
 * alternative is an app where every screen mysteriously refuses.
 */
function NoRole() {
  return (
    <div className="mx-auto max-w-lg py-16">
      <Alert tone="caution" title="Your account is not set up yet">
        Your account exists but has not been given a role. If you signed up as a patient this
        usually resolves within a moment — try reloading. Otherwise contact the coordination team.
      </Alert>
    </div>
  );
}

export function App() {
  const { status } = useAuth();

  // Reports height to a host page when framed; a no-op otherwise.
  useEmbedHeight();

  if (status === "checking") return <Loading label="Checking your session" />;
  if (status === "signed-out") return <SignIn />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="/consultations" element={<Consultations />} />
        <Route path="/consultations/:id" element={<ConsultationDetail />} />
        <Route path="/specialists" element={<Specialists />} />
        <Route path="/specialists/:id" element={<SpecialistDetail />} />
        <Route path="/availability" element={<Availability />} />
        <Route path="/queue" element={<Queue />} />
        <Route path="/staff" element={<Staff />} />
        <Route path="/no-role" element={<NoRole />} />
        {/* The loader points the iframe here. Everything past this point is
            the ordinary app - being embedded changes the chrome, not the
            routes. */}
        <Route path="/embed" element={<Navigate to="/" replace />} />
        {/* Deep links from email land here after a rebuild changes the shape
            of a path; sending them home beats a blank screen. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
